import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'

/**
 * State is never communicated by colour alone: dot (or a 12 px icon) + word, always.
 * Sentence case, never ALL CAPS. See docs/design-system.md §6.5.
 */
type Tone = 'ok' | 'warn' | 'danger' | 'neutral' | 'live'

const tones: Record<Tone, string> = {
  ok: 'bg-ok-soft text-ok border-ok-line',
  warn: 'bg-warn-soft text-warn border-warn-line',
  danger: 'bg-danger-soft text-danger border-danger-line',
  neutral: 'bg-surface-3 text-ink-2 border-hairline',
  live: 'bg-magenta-soft text-magenta border-magenta-line',
}

export function StatusBadge({
  tone,
  icon,
  children,
}: {
  tone: Tone
  /** a 12 px icon in place of the dot (decorative: the word carries the state) */
  icon?: ReactNode
  children: ReactNode
}) {
  return (
    <span
      className={cn(
        'inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-xs border px-2 text-micro',
        tones[tone],
      )}
    >
      {icon ? (
        <span className="inline-flex size-3 shrink-0 items-center justify-center [&>svg]:size-3" aria-hidden>
          {icon}
        </span>
      ) : (
        <span
          // The dot is a background: printed without «background graphics» it vanished and a
          // badge on paper was colour + word only. `exact` keeps it (rule 21, WP-25).
          className={cn(
            'size-1.5 rounded-full bg-current [-webkit-print-color-adjust:exact] [print-color-adjust:exact]',
            tone === 'live' && 'animate-pulse',
          )}
          aria-hidden
        />
      )}
      {children}
    </span>
  )
}
